/**
 * Session 01 and 02 (Figma 10): the password asked for over the page when a
 * session ends mid-work (M03), and the one confirm before signing out (M04 on
 * a phone: a sheet that slides up and stops 8px in).
 */
import { useRef, useState } from "react";
import { Modal as RACModal, ModalOverlay, Dialog, Heading } from "react-aria-components";
import { motion, useReducedMotion } from "motion/react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { SPRING_SMOOTH } from "@/lib/motion";
import { abandonReauth, completeReauth, useSessionGate } from "@/lib/api";
import { GiButton, GiField, GoogleButton, QuietAction } from "./parts";
import { SignInNoticeView } from "./SignInNotice";
import { useSignIn } from "./useSignIn";

/* ------------------------------------------------------------ Session 02 */

const SIGN_OUT_COPY = "This device forgets you. Next time, sign in with your password or Google.";

export function SignOutConfirm({
  isOpen,
  onOpenChange,
  onConfirm,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const phone = useMediaQuery("(max-width: 599px)");
  if (phone) return <SignOutSheet isOpen={isOpen} onOpenChange={onOpenChange} onConfirm={onConfirm} />;
  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Sign out of Creo?"
      description={SIGN_OUT_COPY}
      modalClassName="!w-[min(calc(100vw-2rem),480px)] !max-w-[480px]"
    >
      <div className="flex gap-3 pt-2">
        <GiButton variant="secondary" onClick={() => onOpenChange(false)} className="flex-1">
          Cancel
        </GiButton>
        <GiButton onClick={onConfirm} className="flex-1">
          Sign out
        </GiButton>
      </div>
    </KitModal>
  );
}

/** M04: 0.32 s smooth in and out; a swipe down past a third of its height sends it back. */
function SignOutSheet({
  isOpen,
  onOpenChange,
  onConfirm,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const portal = useCoShellPortal();
  const reduced = useReducedMotion();
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      UNSTABLE_portalContainer={portal ?? undefined}
      className="co-modal-overlay fixed inset-0 z-[var(--z-index-modal)] flex items-end bg-co-scrim p-2"
    >
      <RACModal className="w-full outline-none">
        <motion.div
          initial={reduced ? { opacity: 0 } : { y: "110%" }}
          animate={reduced ? { opacity: 1 } : { y: 0 }}
          transition={reduced ? { duration: 0.16 } : SPRING_SMOOTH}
          drag={reduced ? false : "y"}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 1 }}
          onDragEnd={(_, info) => {
            const height = (info.point.y && window.innerHeight) || 300;
            if (info.offset.y > Math.min(300, height) / 3) onOpenChange(false);
          }}
          className="co-glass-chrome w-full rounded-panel px-5 pb-6 pt-2.5"
        >
          <Dialog className="flex flex-col gap-3.5 outline-none">
            <span aria-hidden className="mx-auto mb-1 mt-1 h-1 w-9 rounded-sm bg-co-faint" />
            <Heading slot="title" className="font-section text-[26px] font-extrabold leading-[29px] text-co-text">
              Sign out of Creo?
            </Heading>
            <p className="text-[15px] leading-[23px] text-co-muted">{SIGN_OUT_COPY}</p>
            <span className="h-2" />
            <GiButton onClick={onConfirm}>Sign out</GiButton>
            <GiButton variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </GiButton>
          </Dialog>
        </motion.div>
      </RACModal>
    </ModalOverlay>
  );
}

/* ------------------------------------------------------------ Session 01 */

/**
 * Mounted once per signed-in page. Opens when lib/api's gate holds a request
 * whose session ended; signing in hands the new pair back and the request goes
 * again. Sign out ends the session the ordinary way.
 */
export function SessionGateDialog({ email, hasPassword }: { email: string | null; hasPassword: boolean }) {
  const open = useSessionGate((s) => s.open);
  const [password, setPassword] = useState("");
  const passwordRef = useRef<HTMLInputElement>(null);
  const form = useSignIn({
    passwordRef,
    onSignedIn: (session) => {
      setPassword("");
      completeReauth(session.token, session.refreshToken);
    },
  });

  return (
    <KitModal
      isOpen={open}
      isDismissable={false}
      isKeyboardDismissDisabled
      hideCloseButton
      title="Sign in again to carry on"
      description="Your session ended. Everything on this page stays as it was."
      modalClassName="!w-[min(calc(100vw-2rem),480px)] !max-w-[480px]"
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (email && password) void form.signIn(email, password);
        }}
      >
        <SignInNoticeView notice={form.notice} />
        {email ? <GiField label="Email" type="email" value={email} locked autoComplete="username" /> : null}
        {hasPassword ? (
          <>
            <GiField
              ref={passwordRef}
              label="Password"
              secret
              autoFocus
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              readOnly={form.pending !== null}
            />
            <div className="flex justify-end">
              <QuietAction to="/forgot-password">Forgot password?</QuietAction>
            </div>
          </>
        ) : (
          <GoogleButton type="button" onClick={form.google} loading={form.pending === "google"} />
        )}
        <div className="flex gap-3">
          <GiButton type="button" variant="secondary" className="flex-1" onClick={abandonReauth}>
            Sign out
          </GiButton>
          {hasPassword ? (
            <GiButton type="submit" className="flex-1" loading={form.pending === "password"} disabled={form.waiting}>
              Sign in
            </GiButton>
          ) : null}
        </div>
      </form>
    </KitModal>
  );
}
